import { useState } from 'react';
import { MagnifyingGlass, PlusCircle } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { DateTimeText, Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';

/**
 * NFs do FORNECEDOR vinculadas à OC — o lado fiscal do confronto.
 *
 * MEDIDO (20/08): os acervos locais de NF-e (nfe_mvp / bling_nfe_full) NÃO
 * contêm notas de compra — são as notas EMITIDAS pela empresa. Por isso o
 * fluxo é: digitar a CHAVE (44) → consultar (valida o DV e tenta o acervo,
 * que normalmente não terá) → completar número/valor do DANFE → registrar.
 * Se a nota um dia existir no acervo, a consulta preenche tudo sozinha e o
 * registro guarda a fonte.
 */
const InboundInvoicesPanel = ({ order, invoices = [], label, onChanged }) => {
    const toast = useToast();
    const [isFormOpen, setFormOpen] = useState(false);
    const [accessKey, setAccessKey] = useState('');
    const [lookup, setLookup] = useState(null);
    const [looking, setLooking] = useState(false);
    const [number, setNumber] = useState('');
    const [issuedAt, setIssuedAt] = useState('');
    const [totalAmount, setTotalAmount] = useState('');
    const [issuerName, setIssuerName] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    const resetForm = () => {
        setAccessKey('');
        setLookup(null);
        setNumber('');
        setIssuedAt('');
        setTotalAmount('');
        setIssuerName('');
        setFormError(null);
    };

    const handleLookup = async () => {
        setLooking(true);
        setFormError(null);
        setLookup(null);
        try {
            const data = await api.lookupWarehouseInboundInvoice(accessKey);
            setLookup(data);
            // Acervo COM a nota: preenche o formulário (o usuário confere).
            const header = data?.archive?.found ? data.archive.header : null;
            setNumber(header?.number || data?.key_fields?.number || '');
            setIssuedAt(header?.issued_at || '');
            setTotalAmount(header?.total_amount != null ? String(header.total_amount) : '');
            setIssuerName(header?.issuer_name || '');
        } catch (error) {
            setFormError(error?.message || 'Erro na consulta');
        } finally {
            setLooking(false);
        }
    };

    const handleRegister = async () => {
        setSaving(true);
        setFormError(null);
        try {
            await api.registerWarehouseInboundInvoice({
                purchase_order_id: order.id,
                access_key: accessKey,
                ...(number.trim() ? { number: number.trim() } : {}),
                ...(issuedAt.trim() ? { issued_at: issuedAt.trim() } : {}),
                ...(totalAmount.trim() ? { total_amount: Number(totalAmount) } : {}),
                ...(issuerName.trim() ? { issuer_name: issuerName.trim() } : {}),
            });
            toast.success(label('wms.inbound.invoice_saved'));
            resetForm();
            setFormOpen(false);
            onChanged?.();
        } catch (error) {
            setFormError(error?.message || 'Erro ao registrar');
        } finally {
            setSaving(false);
        }
    };

    const lookupNotice = lookup ? (
        lookup.registered ? (
            <p className="text-sm text-amber-700 dark:text-amber-300">
                {label('wms.inbound.invoice_registered_elsewhere')}
            </p>
        ) : !lookup.archive?.configured ? (
            <p className="text-sm text-mono-500 dark:text-mono-400">
                {label('wms.inbound.invoice_lookup_unconfigured')}
            </p>
        ) : lookup.archive?.error ? (
            <p className="text-sm text-amber-700 dark:text-amber-300">{lookup.archive.error}</p>
        ) : lookup.archive?.found ? (
            <p className="text-sm text-green-700 dark:text-green-400">{label('wms.inbound.invoice_lookup_found')}</p>
        ) : (
            <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.inbound.invoice_lookup_not_found')}</p>
        )
    ) : null;

    return (
        <section className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
            <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-black dark:text-white">
                    {label('wms.inbound.invoices_title')}
                </h3>
                <Button variant="secondary" size="sm" onClick={() => setFormOpen((previous) => !previous)}>
                    <PlusCircle size={16} className="mr-1.5" />
                    {label('wms.inbound.invoice_add')}
                </Button>
            </div>

            {invoices.length === 0 && !isFormOpen ? (
                <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.inbound.invoices_empty')}</p>
            ) : (
                <ul className="space-y-2">
                    {invoices.map((invoice) => (
                        <li
                            key={invoice.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-mono-100 px-3 py-2 text-sm dark:border-mono-900"
                        >
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                        NF {invoice.number || '—'}
                                        {invoice.series ? `/${invoice.series}` : ''}
                                    </span>
                                    {invoice.archive_source !== 'NONE' ? (
                                        <Badge variant="success">{invoice.archive_source}</Badge>
                                    ) : null}
                                </div>
                                <Mono className="block max-w-full truncate text-xs text-mono-500 dark:text-mono-400">
                                    {invoice.access_key}
                                </Mono>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-mono-600 dark:text-mono-400">
                                {invoice.issuer_name ? <span>{invoice.issuer_name}</span> : null}
                                {invoice.total_amount != null ? (
                                    <Mono className="font-semibold">R$ {invoice.total_amount}</Mono>
                                ) : null}
                                {invoice.issued_at ? <DateTimeText value={invoice.issued_at} /> : null}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {isFormOpen ? (
                <div className="mt-3 space-y-3 rounded-lg border border-mono-200 p-3 dark:border-mono-800">
                    <div className="flex items-end gap-2">
                        <div className="min-w-0 flex-1">
                            <Input
                                label={label('wms.inbound.invoice_key')}
                                value={accessKey}
                                onChange={(event) => setAccessKey(event.target.value)}
                                placeholder="44 dígitos"
                                disabled={looking || saving}
                            />
                        </div>
                        <Button
                            variant="secondary"
                            onClick={handleLookup}
                            loading={looking}
                            disabled={accessKey.replace(/\D/g, '').length !== 44 || saving}
                        >
                            <MagnifyingGlass size={16} className="mr-1.5" />
                            {label('wms.inbound.invoice_lookup')}
                        </Button>
                    </div>

                    {lookupNotice}

                    {lookup && !lookup.registered ? (
                        <>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <Input
                                    label={label('wms.inbound.invoice_number')}
                                    value={number}
                                    onChange={(event) => setNumber(event.target.value)}
                                    disabled={saving}
                                />
                                <Input
                                    label={label('wms.inbound.invoice_issued_at')}
                                    type="date"
                                    value={issuedAt}
                                    onChange={(event) => setIssuedAt(event.target.value)}
                                    disabled={saving}
                                />
                                <Input
                                    label={label('wms.inbound.invoice_total')}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={totalAmount}
                                    onChange={(event) => setTotalAmount(event.target.value)}
                                    disabled={saving}
                                />
                                <Input
                                    label={label('wms.inbound.invoice_issuer')}
                                    value={issuerName}
                                    onChange={(event) => setIssuerName(event.target.value)}
                                    disabled={saving}
                                />
                            </div>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.inbound.invoice_issuer_cnpj')}:{' '}
                                <Mono>{lookup.key_fields?.issuer_cnpj || '—'}</Mono>
                            </p>
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        resetForm();
                                        setFormOpen(false);
                                    }}
                                    disabled={saving}
                                >
                                    {label('wms.common.cancel')}
                                </Button>
                                <Button onClick={handleRegister} loading={saving}>
                                    {label('wms.inbound.invoice_save')}
                                </Button>
                            </div>
                        </>
                    ) : null}

                    {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
                </div>
            ) : null}
        </section>
    );
};

export default InboundInvoicesPanel;
